import { useCallback, useRef, useState } from "react";
import { FileUp, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { uploadResume, bulkUploadResumes } from "@/lib/api";
import { celebrate } from "@/lib/celebrate";
import { toast } from "sonner";

const ACCEPTED = [".pdf", ".docx", ".txt"];

export default function UploadZone({ onUploaded }) {
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null); // {done, total, errors}
  const inputRef = useRef(null);

  const validateFiles = (files) => {
    const ok = [];
    const bad = [];
    for (const f of files) {
      const lower = f.name.toLowerCase();
      if (ACCEPTED.some((ext) => lower.endsWith(ext))) ok.push(f);
      else bad.push(f.name);
    }
    return { ok, bad };
  };

  const handleFiles = useCallback(
    async (fileList) => {
      const files = Array.from(fileList || []);
      if (!files.length) return;
      const { ok, bad } = validateFiles(files);
      if (bad.length) toast.error(`Skipped (unsupported): ${bad.join(", ")}`);
      if (!ok.length) return;

      setUploading(true);
      setProgress({ done: 0, total: ok.length, errors: [] });

      try {
        if (ok.length === 1) {
          const data = await uploadResume(ok[0]);
          toast.success(`✨ Parsed: ${data.full_name || ok[0].name}`);
          celebrate();
          setProgress({ done: 1, total: 1, errors: [] });
        } else {
          const res = await bulkUploadResumes(ok);
          setProgress({
            done: res.successful,
            total: res.total,
            errors: res.errors || [],
          });
          if (res.successful > 0) {
            toast.success(`🎉 Parsed ${res.successful} of ${res.total} resumes`);
            celebrate();
          }
          if (res.errors?.length) {
            toast.error(
              `${res.errors.length} failed: ${res.errors
                .map((e) => e.filename)
                .join(", ")}`
            );
          }
        }
        onUploaded?.();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Upload failed");
      } finally {
        setUploading(false);
        setTimeout(() => setProgress(null), 4000);
      }
    },
    [onUploaded]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  return (
    <div className="swiss-card overflow-hidden" data-testid="upload-zone-wrapper">
      <div className="grid md:grid-cols-[1fr_300px]">
        <label
          htmlFor="resume-file-input"
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          data-testid="resume-drop-zone"
          className={`relative cursor-pointer p-8 md:p-12 flex flex-col items-start justify-center min-h-[280px] transition-colors rounded-l-md ${
            dragOver
              ? "bg-[var(--brand-primary)]/5"
              : "bg-[var(--bg-tint)]/60 hover:bg-[var(--bg-tint)]"
          }`}
        >
          <div
            className="absolute inset-3 border-2 border-dashed rounded-md pointer-events-none transition-colors"
            style={{
              borderColor: dragOver
                ? "var(--brand-accent)"
                : "var(--border-default)",
            }}
          />
          <div className="relative z-10 max-w-md">
            <div className="overline mb-3">single or bulk upload</div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-[var(--text-primary)] mb-3 leading-tight">
              Drop resumes here.
              <br />
              <span className="gradient-text">We do the rest.</span>
            </h2>
            <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-5 max-w-md">
              Drag one or many files into this zone, or browse from your
              device. We strip contact info and return clean, branded PDFs.
            </p>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  inputRef.current?.click();
                }}
                className="btn-primary"
                disabled={uploading}
                data-testid="browse-file-button"
              >
                {uploading ? (
                  <>
                    <span className="spinner" />
                    Parsing…
                  </>
                ) : (
                  <>
                    <FileUp className="w-4 h-4" strokeWidth={2} />
                    Browse files
                  </>
                )}
              </button>
              <span className="text-xs font-medium text-[var(--text-muted)]">
                PDF · DOCX · TXT · multi-select
              </span>
            </div>

            {progress && (
              <div
                className="mt-5 p-3 rounded-md border border-[var(--border-default)] bg-white shadow-sm"
                data-testid="upload-progress"
              >
                <div className="flex items-center justify-between text-xs font-medium">
                  <span className="flex items-center gap-2 text-[var(--text-secondary)]">
                    {uploading ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-3.5 h-3.5 text-[var(--brand-success)]" />
                    )}
                    Parsed {progress.done} / {progress.total}
                  </span>
                  {progress.errors.length > 0 && (
                    <span className="flex items-center gap-1 text-[var(--brand-danger)]">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      {progress.errors.length} failed
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
          <input
            ref={inputRef}
            id="resume-file-input"
            type="file"
            accept=".pdf,.docx,.txt"
            multiple
            className="hidden"
            data-testid="resume-file-input"
            onChange={(e) => handleFiles(e.target.files)}
          />
        </label>

        <div className="border-t md:border-t-0 md:border-l border-[var(--border-default)] bg-white p-7 flex flex-col justify-between">
          <div>
            <div className="overline mb-3">processing spec</div>
            <h3 className="font-heading text-lg font-semibold tracking-tight text-[var(--text-primary)] mb-4">
              What happens next
            </h3>
            <ol className="space-y-3 text-sm">
              {[
                "Extract text from your file",
                "Scrub email · phone · address",
                "Parse with Claude Sonnet 4.5",
                "Render branded PDF & DOCX",
              ].map((t, i) => (
                <li key={t} className="flex gap-3 items-start">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-[var(--brand-primary)]/8 text-[var(--brand-primary)] text-[10px] font-bold flex items-center justify-center mt-0.5">
                    {i + 1}
                  </span>
                  <span className="text-[var(--text-secondary)]">{t}</span>
                </li>
              ))}
            </ol>
          </div>
          <div className="mt-6 p-3 rounded-md bg-[var(--brand-primary)]/5 border border-[var(--brand-primary)]/12">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[var(--brand-success)] mt-0.5 shrink-0" />
              <p className="text-xs text-[var(--text-secondary)] leading-relaxed">
                Average parse time:{" "}
                <span className="font-semibold text-[var(--text-primary)]">
                  ~8 seconds
                </span>{" "}
                per resume.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
